import { useState, useRef } from "react";
import { Camera, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/useAuth";
import { useProfile, useUpdateProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export function AvatarUpload() {
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const updateProfile = useUpdateProfile();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const getInitials = () => {
    if (profile?.full_name) {
      return profile.full_name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
    }
    return user?.email?.slice(0, 2).toUpperCase() || "U";
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Por favor, selecione uma imagem.",
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "A imagem deve ter no máximo 5MB.",
      });
      return;
    }

    setIsUploading(true);

    try {
      // Generate unique filename
      const fileExt = file.name.split(".").pop();
      const fileName = `${user.id}/avatar.${fileExt}`;

      // Delete old avatar if exists
      if (profile?.avatar_url) {
        const oldPath = profile.avatar_url.split("/").slice(-2).join("/");
        await supabase.storage.from("avatars").remove([oldPath]);
      }

      // Upload new avatar
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from("avatars")
        .getPublicUrl(fileName);

      // Add cache buster to URL
      const avatarUrl = `${publicUrl}?t=${Date.now()}`;

      // Update profile
      await updateProfile.mutateAsync({ avatar_url: avatarUrl });

      toast({
        title: "Sucesso!",
        description: "Foto de perfil atualizada.",
      });
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível fazer upload da foto.",
      });
    } finally {
      setIsUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveAvatar = async () => {
    if (!user?.id || !profile?.avatar_url) return;

    setIsUploading(true);

    try {
      // Extract file path from URL
      const urlParts = profile.avatar_url.split("/");
      const filePath = urlParts.slice(-2).join("/").split("?")[0];

      // Delete from storage
      await supabase.storage.from("avatars").remove([filePath]);

      // Update profile
      await updateProfile.mutateAsync({ avatar_url: null });

      toast({
        title: "Sucesso!",
        description: "Foto de perfil removida.",
      });
    } catch (error) {
      console.error("Remove error:", error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível remover a foto.",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="-mt-12 mb-6 flex items-end gap-4">
      <div className="relative">
        <Avatar className="h-24 w-24 border-4 border-background shadow-lg">
          {profile?.avatar_url ? (
            <AvatarImage src={profile.avatar_url} alt={profile.full_name || "Avatar"} />
          ) : null}
          <AvatarFallback className="bg-primary text-primary-foreground text-2xl font-semibold">
            {getInitials()}
          </AvatarFallback>
        </Avatar>
        
        {/* Remove button overlay */}
        {profile?.avatar_url && !isUploading && (
          <button
            onClick={handleRemoveAvatar}
            className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shadow-md hover:bg-destructive/90 transition-colors"
            title="Remover foto"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        
        {/* Loading overlay */}
        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-full">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}
      </div>
      
      <div className="flex flex-col gap-1">
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          disabled={isUploading}
          onClick={() => fileInputRef.current?.click()}
        >
          <Camera className="h-4 w-4" />
          {profile?.avatar_url ? "Trocar foto" : "Adicionar foto"}
        </Button>
        <span className="text-xs text-muted-foreground">
          JPG, PNG ou GIF. Máx 5MB.
        </span>
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
}
